
import React, { useState, useEffect } from "react";
import { useCurrencies } from "@/hooks/useCurrencies";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { toast } from "sonner";
import { Loader2, Check, Search, Star } from "lucide-react";
import { Currency } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

// Replace the imported schemas with an inline schema
const currencyFormSchema = z.object({
  code: z.string().min(2, "Code is required").max(3, "Code should be 2-3 characters"),
  name: z.string().min(2, "Name is required"),
  symbol: z.string().min(1, "Symbol is required"),
});

type CurrencyFormValues = z.infer<typeof currencyFormSchema>;

type CommonCurrency = {
  code: string;
  name: string;
  symbol: string;
};

const COMMON_CURRENCIES: CommonCurrency[] = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "CAD", name: "Canadian Dollar", symbol: "$" },
  { code: "AUD", name: "Australian Dollar", symbol: "$" },
  { code: "CHF", name: "Swiss Franc", symbol: "Fr" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "SEK", name: "Swedish Krona", symbol: "kr" },
  { code: "NOK", name: "Norwegian Krone", symbol: "kr" },
  { code: "DKK", name: "Danish Krone", symbol: "kr" },
  { code: "PLN", name: "Polish Zloty", symbol: "zł" },
  { code: "CZK", name: "Czech Koruna", symbol: "Kč" },
  { code: "HUF", name: "Hungarian Forint", symbol: "Ft" },
  { code: "RON", name: "Romanian Leu", symbol: "lei" },
  { code: "BGN", name: "Bulgarian Lev", symbol: "лв" },
  { code: "HRK", name: "Croatian Kuna", symbol: "kn" },
  { code: "ISK", name: "Icelandic Króna", symbol: "kr" },
  { code: "NZD", name: "New Zealand Dollar", symbol: "$" },
  { code: "MXN", name: "Mexican Peso", symbol: "$" },
  { code: "SGD", name: "Singapore Dollar", symbol: "$" },
  { code: "HKD", name: "Hong Kong Dollar", symbol: "$" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$" },
  { code: "ZAR", name: "South African Rand", symbol: "R" },
  { code: "RUB", name: "Russian Ruble", symbol: "₽" },
  { code: "TRY", name: "Turkish Lira", symbol: "₺" },
];

type UserPreferenceMode = {
  mode?: 'preference';
  onSelect?: never;
  existingCurrencies?: never;
};

type SelectionMode = {
  mode: 'selection';
  onSelect: (selectedCurrency: Omit<Currency, "id" | "user_id">) => void;
  existingCurrencies?: Currency[];
};

type CurrencySelectorProps = UserPreferenceMode | SelectionMode;

export function CurrencySelector(props: CurrencySelectorProps) {
  const { useCurrenciesQuery, useCreateCurrencyMutation } = useCurrencies();
  const { data: allCurrencies = [], isLoading: isLoadingCurrencies } = useCurrenciesQuery();
  const queryClient = useQueryClient();
  const createCurrency = useCreateCurrencyMutation();
  
  const [processing, setProcessing] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  
  const isPreferenceMode = props.mode !== 'selection';
  const { preferences, updatePreferences, isLoading: isLoadingPreferences, useUserPreferencesQuery } = useUserPreferences();
  
  const { data: userPreferences, refetch: refetchUserPreferences } = useUserPreferencesQuery();
  
  const [selectedCurrency, setSelectedCurrency] = useState<string>(
    isPreferenceMode ? (userPreferences?.default_currency_id || preferences?.default_currency_id || "") : ""
  );
  
  useEffect(() => {
    if (isPreferenceMode && userPreferences?.default_currency_id) {
      setSelectedCurrency(userPreferences.default_currency_id);
    } else if (isPreferenceMode && preferences?.default_currency_id) {
      setSelectedCurrency(preferences.default_currency_id);
    }
  }, [isPreferenceMode, userPreferences, preferences]);
  
  const [searchQuery, setSearchQuery] = useState("");
  
  const isLoading = isLoadingCurrencies || (isPreferenceMode && isLoadingPreferences);
  
  const dbCurrencies = isPreferenceMode ? allCurrencies : 
    props.existingCurrencies ? 
      allCurrencies.filter(currency => 
        !props.existingCurrencies?.some(existingCurrency => existingCurrency.code === currency.code)
      ) : 
      allCurrencies;
  
  const allDisplayCurrencies = isPreferenceMode ? dbCurrencies : [
    ...dbCurrencies,
    ...COMMON_CURRENCIES
      .filter(common => 
        !props.existingCurrencies?.some(existing => existing.code === common.code) &&
        !dbCurrencies.some(db => db.code === common.code)
      )
      .map((currency, index) => ({
        ...currency,
        id: `common-${index}`,
        user_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }))
  ];
  
  const displayCurrencies = allDisplayCurrencies.filter(currency => 
    searchQuery === "" || 
    currency.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    currency.code.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleCurrencySelect = async (currency: Currency) => {
    if (isPreferenceMode) {
      setSelectedCurrency(currency.id);
      
      try {
        await updatePreferences({ default_currency_id: currency.id });
        toast.success("Default currency updated successfully");
        
        await refetchUserPreferences();
      } catch (error) {
        console.error("Error updating default currency:", error);
        toast.error("Failed to update default currency");
        
        if (preferences?.default_currency_id) {
          setSelectedCurrency(preferences.default_currency_id);
        }
      }
    } else if (props.onSelect) {
      const { id, user_id, ...currencyData } = currency;
      props.onSelect(currencyData);
    }
  };
  
  const selectedCurrencyObject = displayCurrencies.find(c => c.id === selectedCurrency);
  
  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">Loading currencies...</span>
      </div>
    );
  }
  
  if (displayCurrencies.length === 0 && searchQuery === "") {
    return (
      <div className="text-muted-foreground">
        No currencies available. Please add currencies first.
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {isPreferenceMode && selectedCurrencyObject && (
        <div className="flex items-center gap-3 p-3 rounded-md bg-muted/40">
          <span className="text-lg font-medium">{selectedCurrencyObject.symbol}</span>
          <div className="flex-1">
            <div className="font-medium">{selectedCurrencyObject.name}</div>
            <div className="text-xs text-muted-foreground">{selectedCurrencyObject.code}</div>
          </div>
          <Badge className="ml-auto bg-primary hover:bg-primary">
            <Star className="h-3 w-3 mr-1" />
            Default
          </Badge>
        </div>
      )}
      
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search currencies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>
      
      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
        {displayCurrencies.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No currencies match your search
          </div>
        ) : (
          displayCurrencies.map((currency) => (
            <div 
              key={currency.id} 
              className={cn(
                "flex items-center gap-3 p-3 rounded-md hover:bg-accent cursor-pointer",
                selectedCurrency === currency.id && "bg-accent"
              )}
              onClick={() => handleCurrencySelect(currency)}
            >
              {isPreferenceMode ? (
                <div className="flex items-center w-full justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-medium">{currency.symbol}</span>
                    <div>
                      <div className="font-medium">{currency.name}</div>
                      <div className="text-xs text-muted-foreground">{currency.code}</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {selectedCurrency === currency.id && (
                      <Badge variant="outline" className="mr-2 border-primary">
                        <Star className="h-3 w-3 mr-1 text-primary" />
                        Default
                      </Badge>
                    )}
                    {selectedCurrency === currency.id && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 w-full">
                  <Checkbox 
                    id={`currency-${currency.id}`} 
                    checked={false} 
                    onCheckedChange={() => handleCurrencySelect(currency)}
                    className="translate-y-[1px]"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-lg font-medium">{currency.symbol}</span>
                    <div>
                      <label 
                        htmlFor={`currency-${currency.id}`}
                        className="font-medium cursor-pointer"
                      >
                        {currency.name}
                      </label>
                      <div className="text-xs text-muted-foreground">{currency.code}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Helper function to handle creating a currency (moved from the problematic section)
export const handleCreateCurrency = async (
  values: CurrencyFormValues, 
  createCurrency: any, 
  onSuccess?: (currency: Currency) => void, 
  closeDialog?: () => void
) => {
  let submitting = false;
  try {
    submitting = true;
    const newCurrency = await createCurrency.mutateAsync({
      code: values.code,
      name: values.name,
      symbol: values.symbol,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
    
    if (onSuccess) {
      onSuccess(newCurrency);
    }
    
    if (closeDialog) {
      closeDialog();
    }
    toast.success(`Added ${values.name} (${values.code})`);
  } catch (error) {
    console.error('Error creating currency:', error);
    toast.error('Failed to create currency');
  } finally {
    submitting = false;
  }
};

// Helper function to handle creating a common currency
export const handleCreateCommonCurrency = async (
  currency: CommonCurrency, 
  createCurrency: any,
  queryClient: any,
  onSuccess?: (currency: Currency) => void
) => {
  let processing = '';
  try {
    processing = currency.code;
    const newCurrency = await createCurrency.mutateAsync({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString() 
    });
    
    if (onSuccess) {
      onSuccess(newCurrency);
    }
    
    queryClient.invalidateQueries({ queryKey: ['currencies'] });
    toast.success(`Added ${currency.name} (${currency.code})`);
  } catch (error) {
    console.error('Error creating currency:', error);
    toast.error('Failed to create currency');
  } finally {
    processing = '';
  }
};
